import type { FormFieldInstance } from "@/form/useForm";
import { formatBytes } from "@/utils/formatting/memory";
import Icon from "components/icon";
import ProgressBar from "components/progress-bar";
import { getRefId, type IAttachment, type ISavedDocument } from "findef";
import { unref, computed, ref } from "vue";
import { hashu32_v1, hashu32f_v1, range, uniqueBy } from "math";
import { useFileDrop } from "~/composables/useFileDrop";
import { EUseFileUploadEventType, useFileUpload, type UseFileUpload } from "~/composables/useFileUpload";

const DEBUG_ENABLED: boolean = false as boolean;

const DropArea = defineComponent(() => {
  return () => {
    return (
      <div class="self-stretch w-full h-40 p-8 bg-gray-50 rounded-lg border border-dashed border-offset-[-1px] border-gray-200 inline-flex flex-col justify-center items-center gap-2.5 cursor-pointer">
        <div class="flex flex-col justify-center items-center gap-2.5">
          <div class="w-44 flex flex-col justify-center items-center gap-2 text-gray-500">
            <Icon
              src={async () =>
                await import("assets/img/svg/icons/upload2.svg?url")
              }
              size={"1.5em"}
              fill="currentColor"
            />
          </div>
          <div class="inline-flex justify-center items-center gap-1">
            <div class="justify-center">
              <span class="text-gray-500 text-sm font-medium font-['Inter'] leading-tight">
                Click to upload
              </span>
              <span class="text-gray-500 text-sm font-bold font-['Inter'] leading-tight">
                {" "}
              </span>
              <span class="text-gray-500 text-sm font-medium font-['Inter'] leading-tight">
                or drag and drop
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };
});
 // 'assets/img/svg/icons/checked.svg?url'
const TransactionItem = defineComponent<{
  progress: MaybeRef<number>;
  filename: MaybeRef<string>;
  filesize: MaybeRef<number>;
  success: MaybeRef<boolean>;
  failed: MaybeRef<boolean>;
  canceled: MaybeRef<boolean>;
  isLoading: MaybeRef<boolean>;
  handleCancel?: () => any;
  handleDelete?: () => any;
}>(
  (props) => {
    const progress = computed(() => unref(props.progress));
    const progressFormated = computed(
      () => `${(progress.value * 100.0).toFixed(1)}%`,
    );
    const filename = computed(() => unref(props.filename));
    const filesize = computed(() => unref(props.filesize));
    const fileSizeFormated = computed(() => formatBytes(filesize.value));
    const success = computed(() => unref(props.success));
    const failed = computed(() => unref(props.failed));
    const canceled = computed(() => unref(props.canceled))

    const handleCancel = () => {
      if (canceled.value) return;

      if (props.handleCancel) {
        props.handleCancel();
      }
    }

    const handleDelete = () => {
      if (props.handleDelete) {
        props.handleDelete();
      }
    }

    return () => {
      return (
        <div class="grid grid-cols-[1fr_1fr] items-center w-full py-2 hover:bg-gray-50 transition-colors select-none">
          <div class="grid grid-cols-[max-content_max-content] items-center gap-2">
            <div class="grid items-center justify-center text-gray-500">
              <Icon
                src={async () =>
                  import("assets/img/svg/icons/file-copy2.svg?raw")
                }
                size="1.5rem"
                fill="currentColor"
              />
            </div>
            <div class="grid grid-auto-rows items-center gap-1">
              <div class="text-gray-900 text-sm font-medium leading-tight">
                {filename.value}
              </div>
              <div class="text-gray-500 text-xs font-normal leading-none">
                {fileSizeFormated.value}
              </div>
            </div>
          </div>
          <div class="grid items-center justify-end">
            {
              canceled.value ? (
                <div class="grid grid-cols-[max-content_max-content] items-center gap-2">
                  <div class="grid justify-center items-center text-gray-500 text-xs font-normal leading-none">
                    <div class="w-5 h-5 p-1 bg-gray-500 text-white rounded-full justify-center items-center grid text-center">
                      <Icon src={async () => import('assets/img/svg/icons/canceled.svg?raw')} fill="currentColor" size={'0.75rem'} />
                    </div>
                  </div>
                  <div class="grid items-center justify-end text-gray-500 cursor-pointer" onClick={handleDelete}>
                    <Icon
                      src={async () =>
                        import("assets/img/svg/icons/trash-outline.svg?raw")
                      }
                      size="1rem"
                      fill="currentColor"
                    />
                  </div>
                </div>
              ) : success.value ? (
                <div class="grid grid-cols-[max-content_max-content] items-center gap-2">
                  <div class="grid justify-center items-center text-gray-500 text-xs font-normal leading-none">
                    <div class="w-4 h-4 p-1 bg-emerald-500 text-white rounded-full justify-center items-center grid">
                      <Icon src={async () => import('assets/img/svg/icons/checked.svg?raw')} stroke="white" size={'0.6rem'} />
                    </div>
                  </div>
                  <div class="grid items-center justify-end text-gray-500 cursor-pointer" onClick={handleDelete}>
                    <Icon
                      src={async () =>
                        import("assets/img/svg/icons/trash-outline.svg?raw")
                      }
                      size="1rem"
                      fill="currentColor"
                    />
                  </div>
                </div>
              ) : failed.value ?  (
                <div class="grid grid-cols-[max-content_max-content] items-center gap-2">
                  <div class="grid justify-center items-center text-gray-500 text-xs font-normal leading-none">
                    <div class="w-4 h-4 p-1 bg-red-500 text-white rounded-full justify-center items-center grid">
                      <Icon src={async () => import('assets/img/svg/icons/warn.svg?raw')} fill="currentColor" size={'0.6rem'} />
                    </div>
                  </div>
                  <div class="grid items-center justify-end text-red-500 cursor-pointer" onClick={handleDelete}>
                    <Icon
                      src={async () =>
                        import("assets/img/svg/icons/close_circle.svg?raw")
                      }
                      size="1.25rem"
                      fill="currentColor"
                    />
                  </div>
                </div>
              ) : (
                <div class="grid grid-cols-[max-content_max-content_max-content] items-center gap-2">
                  <div class="justify-center text-gray-500 text-xs font-normal leading-none">
                    {progressFormated.value}
                  </div>
                  <div class="grid items-center">
                    <ProgressBar
                      withBackground
                      class="w-[86px]"
                      value={props.progress}
                      scale100
                      size="xs"
                    />
                  </div>
                  <div class="grid items-center justify-end text-gray-500 cursor-pointer" onClick={handleCancel}>
                    <Icon
                      src={async () =>
                        import("assets/img/svg/icons/close_circle.svg?raw")
                      }
                      size="1.25rem"
                      fill="currentColor"
                    />
                  </div>
                </div>
              )
            }
          </div>
        </div>
      );
    };
  },
  {
    props: [
      "progress",
      "filename",
      "filesize",
      "success",
      "failed",
      "canceled",
      "isLoading",
      "handleCancel",
      "handleDelete"
    ],
  },
);

export const MultiFileUpload = defineComponent((props: {
  uploader?: UseFileUpload,
  dataRef?: FormFieldInstance,
  value?: Array<ISavedDocument<IAttachment>>,
  type?: any; // just for when used in a form
}) => {
  const upload = props.uploader || useFileUpload({ startTransactionsOnDialogClose: true });


  const updateValue = (attachments: Array<IAttachment>) => {
    const dataRef = props.dataRef;
    if (!dataRef) return;

    const dataValue = unref(props.dataRef?.value?.value);
    const oldAttachments = props.value && Array.isArray(props.value) ? props.value : dataValue && Array.isArray(dataValue) ? dataValue : [];
    const nextAttachments = uniqueBy([...oldAttachments, ...attachments], it => getRefId(it));
    dataRef.setValue(nextAttachments);

    /////////////////////////////////////////////////////////////////////////////
    // // Clear completed transactions to prevent duplicates
    // const attachmentNames = new Set(attachments.map(att => att.name));
    // const transactionsToDelete: string[] = [];

    // upload.transactions.value.forEach((transaction, name) => {
    //   const isOK = unref(transaction.isOK);
    //   const isLoading = unref(transaction.isLoading);
    //   const isFailed = unref(transaction.isFailed);
    //   const isCompleted = isOK && !isLoading && !isFailed;

    //   if (isCompleted) {
    //     const transactionFileName = transaction.attachment?.name || name;
    //     if (attachmentNames.has(transactionFileName)) {
    //       transactionsToDelete.push(name);
    //     }
    //   }
    // });

    // transactionsToDelete.forEach(name => {
    //   upload.deleteTransaction(name);
    // });
  }

  const handleClickDropArea = () => {
    upload.openDialog().then(() => {
      const unsub = upload.events.subscribe(EUseFileUploadEventType.FILE_GROUP_FINISHED, (ev) => {
        updateValue(ev.files.map(it => it.attachment));
        queueMicrotask(() => unsub());
      });
    });
  };

  const drop = useFileDrop({
    callback: (files) => {
      upload.uploadFiles(files).then((attachments) => {
        updateValue(attachments);
      });
    }
  });

  const handleCancelTransaction = (name: string) => {
    upload.cancelTransaction(name);
  }

  const handleDeleteTransaction = (name: string) => {
    upload.deleteTransaction(name);
  }

  const fakeFiles = range(4).map((i) => {
    return {
      progress: hashu32f_v1(i * 2 + 3.3),
      filename: `File ${i + 1}`,
      filesize: hashu32_v1(i * 7.1 + 1.25) / 100,
    };
  });

  const handleDeleteExisting = (attachmentId: string) => {
    const dataRef = props.dataRef;
    if (!dataRef) return;

    const dataValue = unref(props.dataRef?.value?.value);
    const currentAttachments = props.value && Array.isArray(props.value) ? props.value : dataValue && Array.isArray(dataValue) ? dataValue : [];

    const newAttachments = currentAttachments.filter((attachment: ISavedDocument<IAttachment>) =>
      getRefId(attachment) !== attachmentId
    );

    dataRef.setValue(newAttachments.length > 0 ? newAttachments : []);
  };

  const handleDelete = (item: any) => {
    if (item.isSpoofed) {
      handleDeleteExisting(item.originalId);
    } else {
      handleDeleteTransaction(item.name);
    }
  };

  const handleCancel = (item: any) => {
    if (item.isSpoofed) {
      handleDeleteExisting(item.originalId);
    } else {
      handleCancelTransaction(item.name);
    }
  };

  const existingAttachments = computed(() => {
    const dataValue = unref(props.dataRef?.value?.value);
    const propsValue = props.value;

    return (dataValue && Array.isArray(dataValue) ? dataValue :
           propsValue && Array.isArray(propsValue) ? propsValue : []) as ISavedDocument<IAttachment>[];
  });


  const allItems = computed(() => {
    const existing = existingAttachments.value.map(attachment => ({
      name: attachment.name || 'Unknown file',
      file: { size: attachment.sizeBytes || 0 },
      progress: ref(1),
      isOK: ref(true),
      isFailed: ref(false),
      isLoading: ref(false),
      isCanceled: ref(false),
      attachment: attachment,
      isSpoofed: true,
      originalId: getRefId(attachment)
    }));

    const allTransactions = Array.from(upload.transactions.value.values());

    return [...existing, ...allTransactions];
  });

  const uniqueItems = computed(() => uniqueBy(allItems.value, it => it.name));

  return () => {
    return (
      <div class="w-full flex flex-col gap-2">
        <div class="w-full flex-none">
          <DropArea onClick={handleClickDropArea} {...drop.props} />
        </div>
        <div class="w-full flex-1">
          {DEBUG_ENABLED &&
            fakeFiles.map((fake) => {
              return (
                <TransactionItem
                  failed={false}
                  isLoading={true}
                  success={false}
                  canceled={false}
                  filesize={fake.filesize}
                  filename={fake.filename}
                  progress={fake.progress}
                />
              );
            })}

          {uniqueItems.value.map((item) => {
            const key = item.isSpoofed ? `existing-${item.originalId}` : `transaction-${item.name}`;

            return (
              <TransactionItem
                key={key}
                failed={unref(item.isFailed)}
                canceled={unref(item.isCanceled)}
                success={unref(item.isOK)}
                isLoading={unref(item.isLoading)}
                filesize={item.file.size}
                filename={item.name}
                progress={item.progress}
                handleCancel={() => handleCancel(item)}
                handleDelete={() => handleDelete(item)}
              />
            );
          })}
        </div>
      </div>
    );
  };
}, { props: ['uploader', 'dataRef', 'value', 'type'] });
